import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API, errorText } from "@/lib/api";
import { trackConversion } from "@/lib/track";
import { PhoneVerify } from "@/components/PhoneVerify";

const inputCls = "mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-[14px] outline-none transition-colors focus:border-[#0E9384] focus:ring-2 focus:ring-teal-100";
const saved = () => { try { return JSON.parse(localStorage.getItem("aor_contact") || "{}") || {}; } catch { return {}; } };

export const InterestDialog = ({ course, onClose, note = "" }) => {
  const [f, setF] = useState({ name: "", email: "", note: "" });
  const [ph, setPh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!course) return;
    const s = saved();
    setF({ name: s.name || "", email: s.email || "", note });
    setDone(false);
  }, [course, note]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!ph) return toast.error("Please verify your mobile number first.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/course-interest`, { course: course.key, name: f.name, email: f.email, phone: ph.phone, phone_token: ph.token, note: f.note || null });
      trackConversion("interest", { transaction_id: `interest-${data?.id}` });
      localStorage.setItem("aor_contact", JSON.stringify({ name: f.name, email: f.email }));
      setDone(true);
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={Boolean(course)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="interest-dialog" data-lenis-prevent className="max-h-[92vh] w-[calc(100vw-24px)] max-w-md overflow-y-auto rounded-2xl">
        {course && (done ? (
          <div data-testid="interest-success" className="py-4 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#0E9384]" />
            <DialogTitle className="font-display mt-3 text-[24px] font-bold text-[#0B1B33]">Thanks, {f.name.split(" ")[0]}!</DialogTitle>
            <DialogDescription className="mt-2 text-[15px] text-slate-700">We've noted your interest in <b>{course.title}</b> and will reply personally with dates, format and fee details. Nothing is booked or charged.</DialogDescription>
            <button data-testid="interest-done-btn" onClick={onClose} className="btn-teal mx-auto mt-5 flex items-center gap-2 rounded-xl px-6 py-3 font-semibold">Done <ArrowRight className="arrow h-4 w-4" /></button>
          </div>
        ) : (
          <form data-testid="interest-form" onSubmit={submit} className="space-y-3">
            <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#0E9384]">Step {course.n} · {course.fee.startsWith("₹") ? `Course fee ${course.fee}` : course.fee}</p>
            <DialogTitle className="font-display text-[24px] font-bold leading-tight text-[#0B1B33]">I'm interested in {course.title}</DialogTitle>
            <DialogDescription className="text-[14px] text-slate-600">Share your details and we'll get back to you personally. No payment is taken here.</DialogDescription>
            <label className="block"><span className="text-[13px] font-semibold text-slate-700">Full Name *</span><input data-testid="interest-name-input" required maxLength={80} value={f.name} onChange={set("name")} className={inputCls} /></label>
            <label className="block"><span className="text-[13px] font-semibold text-slate-700">Email *</span><input data-testid="interest-email-input" required type="email" value={f.email} onChange={set("email")} className={inputCls} /></label>
            <PhoneVerify value={ph} onChange={setPh} testId="interest-phone" />
            <label className="block"><span className="text-[13px] font-semibold text-slate-700">Anything we should know? (optional)</span><input data-testid="interest-note-input" maxLength={300} value={f.note} onChange={set("note")} placeholder="e.g. preferred batch timing" className={inputCls} /></label>
            <button data-testid="interest-submit-btn" disabled={busy} className="btn-teal flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-[16px] font-semibold disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send my interest <ArrowRight className="arrow h-5 w-5" /></>}
            </button>
            <p className="text-center text-[12.5px] text-slate-600">Your details are used only to reply about this course.</p>
          </form>
        ))}
      </DialogContent>
    </Dialog>
  );
};
